import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableContainer,
    Paper,
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Snackbar,
    Alert,
    Chip,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import { deleteCoupon, getAllCoupons } from "../../Apis/CouponAPI";

const GetAdminCoupons = () => {
    const [coupons, setCoupons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedCoupon, setSelectedCoupon] = useState(null);
    const [openConfirm, setOpenConfirm] = useState(false);
    const [couponToDelete, setCouponToDelete] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    const navigate = useNavigate();

    const fetchCoupons = async () => {
        try {
            setLoading(true);
            const response = await getAllCoupons();
            setCoupons(response?.coupons || []);
        } catch (err) {
            setError("Failed to fetch coupons.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCoupons();
    }, []);

    const handleMenuOpen = (event, coupon) => {
        setAnchorEl(event.currentTarget);
        setSelectedCoupon(coupon);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedCoupon(null);
    };

    const handleEdit = (coupon) => {
        navigate(`/editcoupon/${coupon.id || coupon._id}`, { state: { coupon } });
    };

    const handleDeleteClick = (coupon) => {
        setCouponToDelete(coupon);
        setOpenConfirm(true);
        handleMenuClose();
    };

    const handleDelete = async () => {
        try {
            await deleteCoupon(couponToDelete.id || couponToDelete.id);
            setCoupons((prev) =>
                prev.filter((c) => (c.id || c._id) !== (couponToDelete.id || couponToDelete.id))
            );
            setSnackbar({ open: true, message: "Coupon deleted successfully!", severity: "success" });
        } catch (err) {
            setSnackbar({ open: true, message: "Failed to delete coupon.", severity: "error" });
        } finally {
            setOpenConfirm(false);
            setCouponToDelete(null);
        }
    };

    // Helper functions for status display
    const getStatusLabel = (status) => (status ? "Active" : "Inactive");
    const getStatusColor = (status) => (status ? "success" : "error");

    return (
        <Box sx={{ padding: 3, width: "95%", margin: "auto" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                <Typography variant="h4" fontWeight="bold">Coupons</Typography>
                <Button variant="contained" onClick={() => navigate("/getadmincoupons/add")}>+ Add Coupon</Button>
            </Box>

            {loading && (
                <Box display="flex" justifyContent="center" alignItems="center" height={200}>
                    <CircularProgress />
                </Box>
            )}
            {error && <Alert severity="error">{error}</Alert>}

            {!loading && !error && (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>S.No</TableCell>
                                <TableCell>Coupon Code</TableCell>
                                <TableCell>Title</TableCell>
                                <TableCell>Discount</TableCell>
                                <TableCell>Valid From</TableCell>
                                <TableCell>Valid To</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {coupons.map((coupon, index) => (
                                <TableRow key={coupon.id || coupon._id}>
                                    <TableCell>{index + 1}</TableCell>
                                    <TableCell>{coupon.couponCode}</TableCell>
                                    <TableCell>{coupon.title}</TableCell>
                                    <TableCell>{`${coupon.discountValue} (${coupon.discountType})`}</TableCell>
                                    <TableCell>{coupon.validFrom?.split("T")[0]}</TableCell>
                                    <TableCell>{coupon.validTo?.split("T")[0]}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={getStatusLabel(coupon.status)}
                                            color={getStatusColor(coupon.status)}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <IconButton onClick={(e) => handleMenuOpen(e, coupon)}>
                                            <MoreVertIcon />
                                        </IconButton>
                                        <Menu
                                            anchorEl={anchorEl}
                                            open={Boolean(anchorEl) && (selectedCoupon?.id || selectedCoupon?._id) === (coupon.id || coupon._id)}
                                            onClose={handleMenuClose}
                                        >
                                            <MenuItem onClick={() => handleEdit(coupon)}>
                                                <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                                                <ListItemText>Edit</ListItemText>
                                            </MenuItem>
                                            <MenuItem onClick={() => handleDeleteClick(coupon)}>
                                                <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                                                <ListItemText primaryTypographyProps={{ color: "error" }}>Delete</ListItemText>
                                            </MenuItem>
                                        </Menu>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this coupon? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenConfirm(false)}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" variant="contained">Delete</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default GetAdminCoupons;
